<template>
    <div class="add-to-cart-content">
        <div class="add-to-cart-button-row">
            <cool-input :number-arrows="false" class="quantity-field-wrapper">
                <input
                    class="quantity-field"
                    type="number"
                    v-model="quantity"
                    step="1"
                    min="1"
                    @focus="focusQuantity"
                    @blur="unfocusQuantity"
                    @keypress="disableDecimalKeys($event)"
                    :max="
                        stock?.count != null && stock.count > 1
                            ? stock.count
                            : undefined
                    "
                />
            </cool-input>
            <cool-button
                theme="turqoise"
                class="add-to-cart-btn-wrap"
                size="large"
                :busy="loading"
                :busy-size="32"
            >
                <button
                    type="button"
                    @click="addItems()"
                    :disabled="loading || (stock != null && stock.count <= 0)"
                    class="add-to-cart-btn"
                >
                    {{ labels.frontendProductpage.specs.addToCart }}
                </button>
            </cool-button>
        </div>

        <fade-and-slide>
            <div
                class="success-text"
                v-if="!isNullOrWhitespace(cartLink) && cartLink != null"
            >
                <template
                    v-for="textPart in mapLabels(
                        labels.frontendProductpage.specs.addedToCart.split(
                            '{0}'
                        )
                    )"
                >
                    {{ textPart.txt }}
                    <a
                        v-if="textPart.i != textPart.a.length - 1"
                        :href="cartLink"
                        class="cart-link"
                        >{{
                            labels.frontendProductpage.specs.addedToCartName
                        }}</a
                    >
                </template>
            </div>
        </fade-and-slide>
    </div>
</template>

<style scoped lang="scss">
@import "../../../../responsive/style.scss";

.add-to-cart-button-row {
    display: flex;
    flex-direction: row;
}

.quantity-field-wrapper {
    margin-right: 15px;

    .quantity-field {
        height: 100%;
        text-align: center;
        min-width: 100px;
        max-width: 100px;
        width: 100px;
    }
}

.add-to-cart-content {
    display: block;
}

.add-to-cart-btn-wrap {
    flex: 1;
}

.add-to-cart-btn {
    box-sizing: border-box;
    border: 2px solid #02b6b5;
    border-radius: 100px !important;
    padding: 7px 29px;
    min-width: 250px;
    font-size: 15px;
    font-weight: 600 !important;
    cursor: pointer;
    display: block;
    width: 100%;

    @include media-breakpoint-up(lg) {
        display: inline-block;
        //width: unset;
    }

    &:disabled {
        background-color: #cccccc;
        border-color: #cccccc;
        cursor: not-allowed;
    }
}

.success-text {
    padding: 10px;
    text-align: center;
    font-size: 11pt;
    align-items: center;
}

.cart-link {
    text-decoration: underline;
}

.add-row {
    display: flex;
    flex-direction: row;
    justify-content: space-around;
    align-items: center;
}

.quantity-editor {
    display: flex;
    flex-direction: row;
    align-items: stretch;
}

.quantity-modifier {
    border-color: #ddd;
    border-radius: 0 !important;
    width: 3em;
    position: relative;
    background-color: #fff;
    border-style: solid;
    border-width: 1px;
}

.quantity-selector {
    height: 2.5em;
    box-sizing: border-box;
    text-align: center;
    width: 5em;
    border-color: #ddd;
    border-style: solid;
    border-width: 1px;
    padding-top: 5px;
    padding-bottom: 3px;
    border-left-width: 0;
    border-right-width: 0;
}
</style>

<script setup lang="ts">
import axios from "axios";
import { nextTick, ref, type PropType, watch } from "vue";
import FadeAndSlide from "../../shared/FadeAndSlide.vue";
import labels from "../../shared/labels";
import { type AddToCartResponse, afterAddToCart } from "./cart-adding";
import { isNullOrWhitespace } from "../../../../shared/utils/string";
import CoolButton from "../../shared/CoolButton.vue";
import CoolInput from "../../shared/CoolInput.vue";

const props = defineProps({
    productId: {
        type: Number,
        required: true,
    },
    action: {
        type: String,
        required: true,
    },
    optionalAuctionId: {},
    stock: [Object, undefined] as PropType<
        { count: number } | undefined | null
    >,
});

const quantity = ref<number | "">(1);
const cartLink = ref<string | null | undefined>(null);
const loading = ref(false);

watch(
    () => quantity.value,
    (newValue) => {
        if (newValue === "") return;
        if (newValue < 1) {
            nextTick(function () {
                quantity.value = 1;
            });
        } else if (newValue != Math.round(newValue)) {
            const nextValue = Math.round(newValue);
            nextTick(function () {
                quantity.value = nextValue;
            });
        }
    }
);

function focusQuantity() {
    if (quantity.value == 1) quantity.value = "";
}

function unfocusQuantity() {
    if (quantity.value == "") quantity.value = 1;
}

function mapLabels(value: string[]) {
    return value.map((x: string, i: number, a: string[]) => ({
        i: i,
        txt: x,
        a: a,
    }));
}
async function addItems() {
    loading.value = true;
    try {
        if (quantity.value === "" || quantity.value <= 0) return;
        const response = await axios.post<AddToCartResponse>(props.action, {
            productId: props.productId,
            orderQuantity: quantity.value,
            optionalAuctionId: props.optionalAuctionId,
        });

        const data = response.data;
        if (data.success == true) {
            afterAddToCart(props.productId, data.cartItemsCount, data.firstAdd);
            if (data.hasOwnProperty("cartLink")) {
                cartLink.value = data.cartLink;
            }
        } else if (Array.isArray(data.errors) && data.errors.length > 0) {
            alert(data.errors.join("\n"));
        } else {
            alert("Unknown error");
        }
    } finally {
        loading.value = false;
    }
}

function disableDecimalKeys(e: Event) {
    if (!(e instanceof KeyboardEvent)) {
        return;
    }
    if (
        //["Comma","Period", "Slash", "NumpadAdd","NumpadSubtract", "Minus", "KeyE"].includes(e.code)
        [",", ".", "+", "e"].includes(e.key)
    ) {
        e.preventDefault();
    }
}
</script>
